/**
 * Автотеги: чистая логика панели (ползунок порога, фильтры меток, патч кэша). Модуль без
 * runtime-импортов: его напрямую грузит `node --test`.
 *
 * Метка референса бывает ручной и автоматической: `tag_ids` — все метки (сначала ручные по id,
 * затем только-авто по id), `auto_tag_ids` — те из них, что поставил автотег и человек не трогал.
 */
export type Marks = "all" | "manual" | "auto";

type AutoOnly = { auto_tag_ids?: readonly number[] };
type Tagged = { tag_ids: number[]; auto_tag_ids?: number[] };

/** Сколько оценок проходит порог (`scores` по убыванию); равенство проходит. */
export function passCount(scores: readonly number[], threshold: number): number {
  let n = 0;
  for (const s of scores) {
    if (s < threshold) break;
    n += 1;
  }
  return n;
}

/** Границы ползунка порога: в группе отрицательный порог бессмыслен (победитель группы и так положителен). */
export function sliderRange(inGroup: boolean): { min: number; max: number; step: number } {
  return { min: inGroup ? 0 : -0.02, max: 0.08, step: 0.001 };
}

/** Вид автометки на плитке и в фишках: пунктир поверх цвета тега, подсказка. */
export const AUTO_MARK = { outline: "1px dashed currentColor", outlineOffset: "-1px" } as const;
export const AUTO_TITLE = "авто · по промпту";

export const isAuto = (item: AutoOnly, tagId: number): boolean => (item.auto_tag_ids ?? []).includes(tagId);

/** Срез по происхождению метки: «все» — любая, «вручную» — не только-авто, «авто» — только-авто. */
export function byMarks<T extends AutoOnly & { tag_ids?: readonly number[] }>(
  items: T[],
  tagId: number,
  marks: Marks,
): T[] {
  return items.filter((i) => {
    if (!(i.tag_ids ?? []).includes(tagId)) return false;
    return marks === "all" || isAuto(i, tagId) === (marks === "auto");
  });
}

/** Референсы без единого тега группы. */
export function groupMissing<T extends { tag_ids: readonly number[] }>(items: T[], groupTagIds: readonly number[]): T[] {
  return items.filter((i) => !groupTagIds.some((t) => i.tag_ids.includes(t)));
}

/**
 * Ручное «повесить»/«снять»: повесили — метка в `tag_ids`, из `auto_tag_ids` уходит (стала ручной);
 * сняли — уходит из обоих. Порядок `tag_ids`: ручные по id, затем только-авто по id. Объект не мутируется.
 */
export function patchMarks<T extends Tagged>(item: T, tagId: number, add: boolean): T {
  const auto = (item.auto_tag_ids ?? []).filter((t) => t !== tagId);
  const all = new Set(item.tag_ids.filter((t) => t !== tagId));
  if (add) all.add(tagId);
  const autoSet = new Set(auto);
  const byId = (a: number, b: number) => a - b;
  const manual = [...all].filter((t) => !autoSet.has(t)).sort(byId);
  const autoOnly = [...all].filter((t) => autoSet.has(t)).sort(byId);
  return { ...item, tag_ids: [...manual, ...autoOnly], auto_tag_ids: auto };
}

/**
 * `patchMarks` плюс правило группы: ручная метка тега группы вытесняет автометки остальных тегов
 * группы (сервер переводит их в «снято»), поэтому у фото уходят соседи, что стояли только авто.
 * `groupTagIds` — все теги группы вешаемого тега (пусто — тег без группы).
 */
export function patchManual<T extends Tagged>(item: T, tagId: number, add: boolean, groupTagIds: readonly number[]): T {
  let out = patchMarks(item, tagId, add);
  if (!add) return out;
  for (const t of groupTagIds) if (t !== tagId && isAuto(out, t)) out = patchMarks(out, t, false);
  return out;
}

/** Теги группы тега `tagId` (сам он тоже); тег без группы или неизвестный — пусто. */
export function groupTagIds(tags: readonly { id: number; group_id: number | null }[], tagId: number): number[] {
  const group = tags.find((t) => t.id === tagId)?.group_id ?? null;
  return group === null ? [] : tags.filter((t) => t.group_id === group).map((t) => t.id);
}

/** Русский промпт правили после последнего перевода (пустой — нечего переводить). */
export function promptStale(ruNow: string, ruTranslated: string | null): boolean {
  if (ruTranslated === null) return false;
  return ruNow.trim() !== ruTranslated.trim();
}

export const reportLine = (r: { active: number; added: number; dropped: number }): string =>
  `стоит ${r.active} · добавлено ${r.added} · снято ${r.dropped}`;
